/* eslint-disable no-nested-ternary */
import React, {
  useState, useEffect, cloneElement, useCallback, FC,
} from 'react';
import { useUtils, DatePicker } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { useStyles as dayStyles } from '@material-ui/pickers/views/Calendar/Day';
import clsx from 'clsx';
import minDate from 'date-fns/min';
import maxDate from 'date-fns/max';
import { format as fnsFormat } from 'date-fns';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import TodayIcon from '@material-ui/icons/Today';
import FilledInput from '@material-ui/core/FilledInput';
import { PropsI } from './types';
import useStyles from './styles';

const DateRangePicker: FC<PropsI> = ({
  value,
  onChange,
  labelFunc,
  format,
  emptyLabel,
  autoOk,
  onOpen,
  onClose,
  minDateProp,
  maxDateProp,
  open: openForward,
  ...props
}) => {
  const [begin, setBegin] = useState<MaterialUiPickersDate>(value ? value[0] : null);
  const [end, setEnd] = useState<MaterialUiPickersDate>(value ? value[1] : null);
  const [beginInput, setBeginInput] = useState<MaterialUiPickersDate>(value ? value[0] : null);
  const [endInput, setEndInput] = useState<MaterialUiPickersDate>(value ? value[1] : null);
  const [prevBegin, setPrevBegin] = useState<MaterialUiPickersDate>(null);
  const [prevEnd, setPrevEnd] = useState<MaterialUiPickersDate>(null);
  const [hasClicked, setHasClicked] = useState(false);
  const [hover, setHover] = useState<MaterialUiPickersDate>(null);
  const [accepted, setAccepted] = useState(!!begin && !!end);
  const [open, setOpen] = useState(false);

  const memoAfterInit = useCallback(afterInit, []);

  const utils = useUtils();
  const classes = useStyles();
  const baseClasses = dayStyles();
  const min = minDate([begin || hover || new Date(), end || hover || new Date()]);
  const max = maxDate([begin || hover || new Date(), end || hover || new Date()]);
  const isOpen = openForward !== undefined ? openForward : open;

  useEffect(() => {
    memoAfterInit();
  }, [memoAfterInit]);

  const formatDate = (date: MaterialUiPickersDate) => utils.format(date, format || utils.dateFormat);

  return (
    <>
      <FormControl variant="filled" size="small" className={classes.select}>
        <InputLabel shrink htmlFor="filter-date">Date</InputLabel>
        <FilledInput
          value={beginInput && endInput ? `${fnsFormat(beginInput, 'yyyy/MM/dd')} to ${fnsFormat(endInput, 'yyyy/MM/dd')}` : ''}
          onChange={handleOpen}
          onClick={handleOpen}
          name="date"
          id="filter-date"
          endAdornment={(
            <InputAdornment position="end">
              <IconButton size="small" className={classes.selectBtn} onClick={handleOpen}>
                <TodayIcon />
              </IconButton>
            </InputAdornment>
          )}
        />
      </FormControl>
      <DatePicker
        {...props}
        value={begin}
        renderDay={renderDay}
        open={isOpen}
        onOpen={handleOpen}
        onAccept={handleAccept}
        onClose={handleClose}
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onChange={() => {}}
        labelFunc={handleLabelFunc}
        className={classes.calendar}
        minDate={minDateProp}
        maxDate={maxDateProp}
      />
    </>
  );

  function afterInit() {
    // Only way to get to this state is is openForward is used
    if (isOpen && accepted && !prevBegin && !prevEnd) {
      setAccepted(false);
      setPrevBegin(begin);
      setPrevEnd(end);
      return;
    }
    // Closed without accepting, reset to prev state, don't find onChange
    if (!isOpen && !accepted) {
      setBegin(prevBegin);
      setEnd(prevEnd);
      setHover(null);
      setHasClicked(false);
    }
    // Auto ok and hasn't been accepted, but has all the items set, accept and close.
    // This will also triger the on change event by setting isOpen to false
    if (isOpen && autoOk && !accepted && begin && end && hasClicked) {
      setAccepted(true);
      onClose ? onClose() : setOpen(false);
    }
    if (accepted && begin && end && !isOpen && hasClicked) {
      setHasClicked(false);
      onChange([begin, end]);
      onClose ? onClose() : setOpen(false);
    }
  }

  function handleLabelFunc(date: MaterialUiPickersDate | MaterialUiPickersDate[], invalid: string) {
    if (!isOpen) {
      if (labelFunc) {
        return labelFunc([begin, end], invalid);
      }
      if (begin && end) {
        return `${formatDate(begin)} - ${formatDate(end)}`;
      }
      return emptyLabel || '';
    }

    if (isOpen && prevBegin && prevEnd) {
      if (labelFunc) {
        return labelFunc([prevBegin, prevEnd], invalid);
      }
      return `${formatDate(prevBegin)} - ${formatDate(prevEnd)}`;
    }

    return emptyLabel || '';
  }

  function handleOpen() {
    setAccepted(false);
    setPrevBegin(begin);
    setPrevEnd(end);
    onOpen ? onOpen() : setOpen(true);
  }

  function handleAccept() {
    if (!begin || !end) {
      if (hover && utils.isBeforeDay(begin, hover)) {
        setEnd(hover);
        handleChange(begin, hover);
      } else {
        setEnd(begin);
        setBegin(hover);
        handleChange(hover, begin);
      }
    } else {
      handleChange(begin, end);
    }
    setPrevBegin(null);
    setPrevEnd(null);

    if (!autoOk) {
      setAccepted(true);
    }
  }

  function handleClose() {
    onClose ? onClose() : setOpen(false);
  }

  function handleChange(dateBegin: MaterialUiPickersDate, dateEnd: MaterialUiPickersDate) {
    if (onChange) {
      onChange([dateBegin, dateEnd]);
      setBeginInput(dateBegin);
      setEndInput(dateEnd);
    }
  }

  function renderDay(day: MaterialUiPickersDate, _: MaterialUiPickersDate, __: boolean, dayComponent: JSX.Element) {
    const newDay = day || 0;
    return cloneElement(dayComponent, {
      onClick: handleClick(day),
      onMouseEnter: () => setHover(day),
      selected: newDay >= min && newDay <= max,
      className: getClassName(day, dayComponent),
    });
  }

  function getClassName(day: MaterialUiPickersDate, dayComponent: JSX.Element) {
    const newDay = day || 0;
    return clsx(classes.day, [
      dayComponent.props.hidden && [baseClasses.hidden],
      dayComponent.props.current && [baseClasses.current],
      dayComponent.props.disabled && [baseClasses.dayDisabled],
      newDay >= min && newDay <= max && [baseClasses.daySelected],
      utils.isSameDay(day, min) && [classes.beginCap],
      utils.isSameDay(day, max) && [classes.endCap],
    ]);
  }

  function handleClick(day: MaterialUiPickersDate) {
    return (e: MouseEvent) => {
      setHasClicked(true);
      e.stopPropagation();
      if (!begin) setBegin(day);
      else if (!end) {
        if (utils.isBeforeDay(day, begin)) {
          setEnd(begin);
          setBegin(day);
        } else {
          setEnd(day);
        }
        if (autoOk) {
          setPrevBegin(null);
          setPrevEnd(null);
        }
      } else {
        setBegin(day);
        setEnd(null);
      }
    };
  }
};

export default DateRangePicker;
